// src/components/ProviderSelectionModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config";
import InitialFeedbackModal from "./InitialFeedbackModal";
import BackButton from "./BackButton";
import "./ProviderSelectionModal.css";

const PAGE_SIZE = 40;
const REGION_ORDER = [
  "NCR", "CAR", "Region I", "Region II", "Region III", "Region IV-A", "Region IV-B",
  "Region V", "Region VI", "Region VII", "Region VIII", "Region IX", "Region X",
  "Region XI", "Region XII", "Region XIII", "ARMM", "BARMM", "Other",
];

// The list has literal "null" strings and ", ," gaps for empty parts
const clean = (value) =>
  value == null || value === "null"
    ? ""
    : String(value).replace(/\s*,(\s*,)+/g, ",").replace(/\s+/g, " ").trim();

const normalizeProvider = (raw) => ({
  code: raw.provider_code,
  name: clean(raw["Hospital/Clinic"] || raw.provider_name),
  type: raw["Provider Type"] || "",
  region: clean(raw.Region) || "Other",
  province: clean(raw.Province),
  address: clean(raw.Address),
});

const regionRank = (region) => REGION_ORDER.indexOf(region) + 1 || REGION_ORDER.length + 1;

const TypeBadge = ({ type }) => (
  <span
    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
      type === "Hospital" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"
    }`}
  >
    {type || "Provider"}
  </span>
);

const ProviderSelectionModal = ({ isOpen, onClose }) => {
  const { encryptedData } = useAuth();

  const [providers, setProviders] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [region, setRegion] = useState("");
  const [province, setProvince] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isInitialFeedbackModalOpen, setIsInitialFeedbackModalOpen] = useState(false);

  const searchRef = useRef(null);
  const listRef = useRef(null);

  // One request for the whole list, then everything is instant on the device
  const loadProviders = useCallback(async () => {
    setStatus("loading");
    try {
      const response = await fetch(`${API_BASE_URL}/providers?search=&page=1&per_page=5000`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rows = await response.json();
      setProviders(rows.map(normalizeProvider).filter((provider) => provider.name));
      setStatus("ready");
    } catch (error) {
      console.error("Error fetching providers:", error);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (isOpen && status === "idle") loadProviders();
  }, [isOpen, status, loadProviders]);

  useEffect(() => {
    if (isOpen && status === "ready") searchRef.current?.focus();
  }, [isOpen, status]);

  const regions = useMemo(
    () =>
      [...new Set(providers.map((provider) => provider.region))].sort(
        (a, b) => regionRank(a) - regionRank(b) || a.localeCompare(b)
      ),
    [providers]
  );
  const provinces = useMemo(
    () =>
      [...new Set(
        providers
          .filter((provider) => !region || provider.region === region)
          .map((provider) => provider.province)
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b)),
    [providers, region]
  );

  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const results = useMemo(
    () =>
      providers.filter((provider) => {
        if (type && provider.type !== type) return false;
        if (region && provider.region !== region) return false;
        if (province && provider.province !== province) return false;
        if (!words.length) return true;
        const haystack = `${provider.name} ${provider.address} ${provider.province}`.toLowerCase();
        return words.every((word) => haystack.includes(word));
      }),
    [providers, type, region, province, words]
  );
  const visible = results.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setActiveIndex(-1);
  }, [query, type, region, province]);

  const clearFilters = () => {
    setQuery("");
    setType("");
    setRegion("");
    setProvince("");
    searchRef.current?.focus();
  };

  // Arrow keys move through the list; Enter picks; Escape backs out
  const onSearchKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!visible.length) return;
      const next =
        event.key === "ArrowDown"
          ? Math.min(activeIndex + 1, visible.length - 1)
          : Math.max(activeIndex - 1, 0);
      setActiveIndex(next);
      listRef.current?.children[next]?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter" && activeIndex >= 0 && visible[activeIndex]) {
      event.preventDefault();
      setSelected(visible[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (confirming) setConfirming(false);
      else onClose();
    }
  };

  const submitProvider = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);

    const formData = new FormData();
    formData.append("provider_code", selected.code);
    formData.append("data", encryptedData);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/x1bf/x1wh/generate-approval-redirect-url`,
        formData
      );

      if (response.data.status === "success") {
        let timerInterval;
        Swal.fire({
          title: "Please wait",
          html: "Redirecting you in <b></b> milliseconds.",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
          didOpen: () => {
            Swal.showLoading();
            const timer = Swal.getPopup().querySelector("b");
            timerInterval = setInterval(() => {
              timer.textContent = `${Swal.getTimerLeft()}`;
            }, 100);
          },
          willClose: () => clearInterval(timerInterval),
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.timer) {
            window.location.href = response.data.url;
          }
        });
      } else {
        Swal.fire("Error", "Failed to submit provider.", "error");
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "An unexpected error occurred.",
        "error"
      );
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="provider-overlay fixed inset-0 z-[1000] flex items-end justify-center bg-slate-900/60 backdrop-blur-sm sm:items-center sm:p-4"
        onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="provider-title"
          className="provider-sheet relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-h-[85vh] sm:max-w-xl sm:rounded-2xl"
        >
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3 px-5 pb-3 pt-4">
            <div className="modal-backrow"><BackButton onClick={confirming ? () => setConfirming(false) : onClose} /></div>
            <div className="min-w-0 flex-1">
              <h2 id="provider-title" className="text-xl font-bold text-slate-900">
                Generate LOA
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Choose the accredited hospital or clinic you're going to.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          {/* Search and filters */}
          <div className="space-y-3 border-b border-slate-100 px-5 pb-3">
            <div className="relative">
              <i
                className="fas fa-search pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              ></i>
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search by name, city or address"
                aria-label="Search providers"
                autoComplete="off"
                disabled={status !== "ready"}
                className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-600/15 disabled:opacity-60"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {[
                ["", "All"],
                ["Hospital", "Hospitals"],
                ["Clinic", "Clinics"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setType(value)}
                  aria-pressed={type === value}
                  className={`h-9 rounded-full border px-3.5 text-sm font-semibold transition ${
                    type === value
                      ? "border-green-700 bg-green-700 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-green-600 hover:text-green-800"
                  }`}
                >
                  {label}
                </button>
              ))}
              {/* Full-width row on phones; tucks beside the chips on wider screens */}
              <div className="flex w-full gap-2 sm:ml-auto sm:w-auto">
                <select
                  value={region}
                  onChange={(event) => {
                    setRegion(event.target.value);
                    setProvince("");
                  }}
                  disabled={status !== "ready"}
                  aria-label="Region"
                  className="provider-select h-9 min-w-0 flex-1 appearance-none rounded-full border border-slate-200 bg-white px-3 pr-8 text-sm text-slate-700 outline-none focus:border-green-600 sm:w-36"
                >
                  <option value="">All regions</option>
                  {regions.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
                <select
                  value={province}
                  onChange={(event) => setProvince(event.target.value)}
                  disabled={status !== "ready" || provinces.length === 0}
                  aria-label="Province"
                  className="provider-select h-9 min-w-0 flex-1 appearance-none rounded-full border border-slate-200 bg-white px-3 pr-8 text-sm text-slate-700 outline-none focus:border-green-600 disabled:opacity-50 sm:w-40"
                >
                  <option value="">All provinces</option>
                  {provinces.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="provider-list flex-1 overflow-y-auto px-3 py-3">
            {status === "error" && (
              <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center" role="alert">
                <p className="font-semibold text-slate-900">We couldn't load the provider list.</p>
                <p className="mt-1 text-sm text-slate-500">Check your connection and try again.</p>
                <button
                  type="button"
                  onClick={loadProviders}
                  className="mt-4 h-11 rounded-xl bg-green-700 px-5 font-semibold text-white hover:bg-green-800"
                >
                  Try again
                </button>
              </div>
            )}

            {(status === "idle" || status === "loading") && (
              <ul className="space-y-2" aria-label="Loading providers">
                {[0, 1, 2, 3, 4].map((item) => (
                  <li key={item} className="animate-pulse rounded-2xl border border-slate-100 p-4">
                    <div className="h-4 w-2/3 rounded bg-slate-200" />
                    <div className="mt-3 h-3 w-full rounded bg-slate-100" />
                    <div className="mt-2 h-3 w-1/2 rounded bg-slate-100" />
                  </li>
                ))}
              </ul>
            )}

            {status === "ready" && (
              <>
                <p className="px-1 pb-2 text-xs font-medium uppercase tracking-wide text-slate-400" role="status">
                  {results.length.toLocaleString()} {results.length === 1 ? "provider" : "providers"}
                  {results.length > visible.length ? ` · showing ${visible.length}` : ""}
                </p>

                {results.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center">
                    <p className="font-semibold text-slate-900">No providers match your search.</p>
                    <p className="mt-1 text-sm text-slate-500">Try the city or a shorter name.</p>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-3 font-semibold text-green-700 underline hover:text-green-800"
                    >
                      Clear search and filters
                    </button>
                  </div>
                ) : (
                  <ul ref={listRef} role="listbox" aria-label="Accredited providers" className="space-y-2">
                    {visible.map((provider, index) => {
                      const isSelected = selected?.code === provider.code;
                      return (
                        <li
                          key={provider.code}
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => {
                            setSelected(provider);
                            setActiveIndex(index);
                          }}
                          className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                            isSelected
                              ? "border-green-600 bg-green-50 ring-2 ring-green-600/20"
                              : activeIndex === index
                              ? "border-green-400 bg-slate-50"
                              : "border-slate-200 hover:border-green-400 hover:bg-slate-50"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold leading-snug text-slate-900">{provider.name}</span>
                              <TypeBadge type={provider.type} />
                            </div>
                            {provider.address && (
                              <p className="mt-1 text-sm leading-snug text-slate-500">{provider.address}</p>
                            )}
                            {(provider.province || provider.region !== "Other") && (
                              <p className="mt-1 text-xs font-medium text-slate-400">
                                <i className="fas fa-map-marker-alt mr-1" aria-hidden="true"></i>
                                {[provider.province, provider.region].filter(Boolean).join(" · ")}
                              </p>
                            )}
                          </div>
                          <span
                            aria-hidden="true"
                            className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 transition ${
                              isSelected ? "border-green-600 bg-green-600 text-white" : "border-slate-300 text-transparent"
                            }`}
                          >
                            <i className="fas fa-check text-xs"></i>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {results.length > visible.length && (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                    className="mt-3 h-11 w-full rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:border-green-600 hover:text-green-800"
                  >
                    Show more ({(results.length - visible.length).toLocaleString()} left)
                  </button>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="provider-footer border-t border-slate-100 bg-white px-5 py-3">
            {selected ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Selected</p>
                  <p className="truncate font-semibold text-slate-900">{selected.name}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="h-11 rounded-xl px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="h-11 flex-1 rounded-xl bg-green-700 px-5 font-semibold text-white shadow-sm transition hover:bg-green-800 sm:flex-none"
                  >
                    Generate LOA
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-center text-sm text-slate-500">
                Tap a hospital or clinic to continue.
              </p>
            )}
          </div>

          {/* Confirmation step */}
          {confirming && selected && (
            <div className="absolute inset-0 z-10 flex items-end bg-slate-900/40" onMouseDown={(event) => event.target === event.currentTarget && setConfirming(false)}>
              <div className="provider-confirm w-full rounded-t-3xl bg-white p-5 shadow-2xl" role="alertdialog" aria-labelledby="confirm-title">
                <h3 id="confirm-title" className="text-lg font-bold text-slate-900">Is this the correct provider?</h3>
                <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{selected.name}</span>
                    <TypeBadge type={selected.type} />
                  </div>
                  {selected.address && <p className="mt-1 text-sm text-slate-600">{selected.address}</p>}
                </div>
                <p className="mt-3 text-sm text-slate-500">
                  Your Letter of Authorization will be generated for this provider.
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    disabled={submitting}
                    className="h-12 flex-1 rounded-xl border border-slate-200 font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={submitProvider}
                    disabled={submitting}
                    className="h-12 flex-1 rounded-xl bg-green-700 font-semibold text-white shadow-sm hover:bg-green-800 disabled:opacity-70"
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin mr-2" aria-hidden="true"></i>
                        Generating…
                      </>
                    ) : (
                      "Proceed"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <InitialFeedbackModal
        isOpen={isInitialFeedbackModalOpen}
        onClose={() => setIsInitialFeedbackModalOpen(false)}
      />
    </>
  );
};

export default ProviderSelectionModal;
